/**
 * HeldWinsPanel (24451)
 *
 * Share a Win posts that the automated content check held. A held win has no journal row,
 * so it is not in the posts queue; an admin approves it (posts it to the feed) or rejects it
 * (closes it, never posted).
 *
 * States handled: loading, empty, error, paginated data, per-row action in flight.
 */

import { useEffect, useState } from 'react';
import { CheckCircle, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import {
  approveHeldWin,
  getHeldWins,
  rejectHeldWin,
} from '../../services/adminPostModerationService';
import type { HeldWinRow } from '../../types/heldWin';

const PAGE_SIZE = 20;

const NO_APPLICATION_REASON =
  'This win was held before its app was recorded, so it cannot be posted. Reject it instead.';

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function HeldWinsPanel() {
  const [rows, setRows] = useState<HeldWinRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getHeldWins(page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) return;
        if (result.items.length === 0 && result.totalCount > 0 && page > 0) {
          setPage(page - 1);
          return;
        }
        setRows(result.items);
        setTotalCount(result.totalCount);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load held wins');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, reloadKey]);

  function retry() {
    setLoading(true);
    setReloadKey((k) => k + 1);
  }

  function goToPage(next: number) {
    setLoading(true);
    setPage(next);
  }

  async function decide(row: HeldWinRow, kind: 'approve' | 'reject') {
    const label = row.winType ?? 'win';
    const question =
      kind === 'approve'
        ? `Approve this held ${label}? It will be posted to the feed.`
        : `Reject this held ${label}? It will be closed and never posted.`;
    if (!window.confirm(question)) return;

    setBusyId(row.milestoneRecordId);
    setActionError(null);
    setStatus(null);
    try {
      if (kind === 'approve') {
        await approveHeldWin(row.milestoneRecordId);
      } else {
        await rejectHeldWin(row.milestoneRecordId);
      }
      // Refetch rather than trim locally: removing the last row of a later page must step back
      // a page, or the remaining wins sit on a page the pager no longer offers.
      if (page > 0 && rows.length === 1) setPage(page - 1);
      setReloadKey((k) => k + 1);
      setStatus(kind === 'approve' ? 'Win approved and posted to the feed.' : 'Win rejected.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `Could not ${kind} the win.`);
      // The service surfaces only the message, so any failure (notably a 409 from another admin
      // acting first) refreshes the list rather than leaving a stale row.
      setReloadKey((k) => k + 1);
    } finally {
      setBusyId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">
        Share a Win posts held by the automated content check. Approve to post to the feed, or
        reject to close.
      </p>

      {status && (
        <div role="status" className="rounded-lg border border-emerald-900 bg-emerald-950/40 px-4 py-3 text-sm text-emerald-300">
          {status}
        </div>
      )}
      {actionError && (
        <div role="alert" className="rounded-lg border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
          {actionError}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 text-zinc-500" aria-busy="true" aria-label="Loading held wins">
          <Loader2 size={24} className="animate-spin" aria-hidden="true" />
        </div>
      )}

      {!loading && error && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-lg border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
          <span>{error}</span>
          <button
            type="button"
            onClick={retry}
            className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 flex items-center gap-1"
          >
            <RefreshCw size={14} aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-2">
          <CheckCircle size={40} className="text-zinc-700" aria-hidden="true" />
          <p className="text-sm">No held wins.</p>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="rounded-lg border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm" aria-label="Held wins">
            <thead>
              <tr className="bg-zinc-900 text-zinc-400 text-left text-xs uppercase tracking-wide">
                <th scope="col" className="px-4 py-3 font-medium">Win type</th>
                <th scope="col" className="px-4 py-3 font-medium">Description</th>
                <th scope="col" className="px-4 py-3 font-medium">Submitted</th>
                <th scope="col" className="px-4 py-3 font-medium">Profile</th>
                <th scope="col" className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const id = row.milestoneRecordId;
                const busy = busyId === id;
                const approveBlocked = !row.canApprove;
                return (
                  <tr key={id} className="border-t border-zinc-800 align-top">
                    <td className="px-4 py-3 text-zinc-200">{row.winType ?? 'Win'}</td>
                    <td className="px-4 py-3 text-zinc-300 max-w-md break-words">
                      {row.description ?? <span className="text-zinc-600">[no description]</span>}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{formatDate(row.insertDate)}</td>
                    <td className="px-4 py-3 text-zinc-500 font-mono text-xs" title={row.profileId}>
                      {shortId(row.profileId)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busy || approveBlocked}
                          title={approveBlocked ? NO_APPLICATION_REASON : undefined}
                          aria-label={`Approve held win ${id}`}
                          onClick={() => void decide(row, 'approve')}
                          className="min-h-[48px] px-3 rounded bg-violet-600 hover:bg-violet-500 text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          aria-label={`Reject held win ${id}`}
                          onClick={() => void decide(row, 'reject')}
                          className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 disabled:opacity-50 transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                      {approveBlocked && (
                        <p className="mt-1 text-xs text-zinc-500">{NO_APPLICATION_REASON}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-zinc-500">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
            className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50 flex items-center gap-1"
          >
            <ChevronLeft size={16} aria-hidden="true" />
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
            className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50 flex items-center gap-1"
          >
            Next
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
